import { AxiosStatusCode } from 'smg/axios';
import * as buttons from 'smg/buttons';
import * as models from 'smg/customer-api/models';
import * as session from 'smg/customer-api/session';
import { ReportValidity } from 'smg/form';
import { Phone } from 'smg/phone';

import { AfterLoginPath, CarryReturn, PromptLoginCode } from '../login-code';

main();

type tab = 'phone' | 'email' | 'password';

function main() {
    CarryReturn();

    // ── Elements ──

    const $tabRow = document.querySelector('#login-tab-row') as HTMLElement;
    const $tabs = Array.from($tabRow.querySelectorAll<HTMLElement>('.login-tab'));
    const $panels: Record<tab, HTMLElement> = {
        phone: document.querySelector('#tab-phone') as HTMLElement,
        email: document.querySelector('#tab-email') as HTMLElement,
        password: document.querySelector('#tab-password') as HTMLElement,
    };

    const $phoneForm = document.querySelector('#phone-form') as HTMLFormElement;
    const $phoneInput = document.querySelector('#phone') as HTMLInputElement;

    const $emailForm = document.querySelector('#email-form') as HTMLFormElement;
    const $emailInput = document.querySelector('#login-email') as HTMLInputElement;

    const $passwordForm = document.querySelector('#password-form') as HTMLFormElement;
    const $pwEmail = document.querySelector('#pw-email') as HTMLInputElement;
    const $pwPassword = document.querySelector('#pw-password') as HTMLInputElement;

    const $alert = document.querySelector('#login-alert') as HTMLElement;

    // ── Phone input (intl-tel-input) ──

    const phone = new Phone($phoneInput);

    // ── Alert helpers ──

    function showAlert(msg: string) {
        hideAlert();
        const $msg = $alert.querySelector('.msg');
        if ($msg) {
            $msg.textContent = msg;
        }
        $alert.classList.remove('d-none');
    }

    function hideAlert() {
        $alert.classList.add('d-none');
    }

    // ── Tab switching ──

    let activeTab: tab = 'phone';

    function switchTab(t: tab) {
        if (t === activeTab) {
            return;
        }
        hideAlert();
        activeTab = t;

        // Sync email value between email and password tabs
        if (t === 'email' && $pwEmail.value && !$emailInput.value) {
            $emailInput.value = $pwEmail.value;
        } else if (t === 'password' && $emailInput.value && !$pwEmail.value) {
            $pwEmail.value = $emailInput.value;
        }

        for (const $tab of $tabs) {
            const isActive = $tab.dataset.tab === t;
            $tab.classList.toggle('active', isActive);
            $tab.setAttribute('aria-selected', String(isActive));
        }

        for (const [key, $panel] of Object.entries($panels)) {
            $panel.classList.toggle('d-none', key !== t);
        }

        const url = t === 'phone' ?
            window.location.pathname + window.location.search :
            window.location.pathname + window.location.search + '#' + t;
        history.replaceState(null, '', url);
    }

    for (const $tab of $tabs) {
        $tab.addEventListener('click', () => switchTab($tab.dataset.tab as tab));
        $tab.addEventListener('keydown', e => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                switchTab($tab.dataset.tab as tab);
            }
        });
    }

    // Hash-based deep linking
    const hash = window.location.hash.replace('#', '') as tab;
    if (hash === 'email' || hash === 'password') {
        switchTab(hash);
    }

    // ── Tab 1: Phone form ──

    $phoneForm.addEventListener('submit', async e => {
        e.preventDefault();
        if (!ReportValidity($phoneForm)) {
            return;
        }
        hideAlert();

        const $fieldset = $phoneForm.querySelector('fieldset') as HTMLFieldSetElement;
        const $submit = $phoneForm.querySelector('[type="submit"]') as HTMLButtonElement;
        $fieldset.setAttribute('disabled', 'disabled');
        buttons.Start($submit);

        try {
            const prompt = await PromptLoginCode({ phone });

            void prompt.createCode.finally(() => {
                $fieldset.removeAttribute('disabled');
                buttons.Stop($submit);
            });

            void prompt.login.
                then(() => window.location.href = AfterLoginPath()).
                catch(err => {
                    showAlert('Something went wrong. Please try again.');
                    throw err;
                });
        } catch (err) {
            $fieldset.removeAttribute('disabled');
            buttons.Stop($submit);
            showAlert('Something went wrong. Please try again.');
            throw err;
        }
    });

    // ── Tab 2: Email form ──

    $emailForm.addEventListener('submit', async e => {
        e.preventDefault();
        if (!ReportValidity($emailForm)) {
            return;
        }
        hideAlert();

        const $fieldset = $emailForm.querySelector('fieldset') as HTMLFieldSetElement;
        const $submit = $emailForm.querySelector('[type="submit"]') as HTMLButtonElement;
        $fieldset.setAttribute('disabled', 'disabled');
        buttons.Start($submit);

        try {
            const prompt = await PromptLoginCode({ email: $emailInput.value });

            void prompt.createCode.finally(() => {
                $fieldset.removeAttribute('disabled');
                buttons.Stop($submit);
            });

            void prompt.login.
                then(() => window.location.href = AfterLoginPath()).
                catch(err => {
                    showAlert('Something went wrong. Please try again.');
                    throw err;
                });
        } catch (err) {
            $fieldset.removeAttribute('disabled');
            buttons.Stop($submit);
            showAlert('Something went wrong. Please try again.');
            throw err;
        }
    });

    // ── Tab 3: Password form ──

    $passwordForm.addEventListener('submit', e => {
        e.preventDefault();
        if (!ReportValidity($passwordForm)) {
            return;
        }
        hideAlert();

        const $fieldset = $passwordForm.querySelector('fieldset') as HTMLFieldSetElement;
        const $submit = $passwordForm.querySelector('[type="submit"]') as HTMLButtonElement;
        $fieldset.setAttribute('disabled', 'disabled');
        buttons.Start($submit);

        void session.CreateSession({
            'email': $pwEmail.value,
            'password': $pwPassword.value,
        }).then(r => {
            const resp = models.SessionCreateResponse.fromJSON(r.data);
            session.SetLoginToken(resp.token);
            window.location.href = AfterLoginPath();
        }).catch(err => {
            $fieldset.removeAttribute('disabled');
            buttons.Stop($submit);

            const status = AxiosStatusCode(err);
            if (status === 400 || status === 401) {
                showAlert('Invalid email or password.');
                return;
            }

            showAlert('Something went wrong. Please try again.');
        });
    });
}
